import {
	DisciplineEnum,
	DisciplineOptions,
	HomeHealthAideInterventionFrequencyTypeOptions,
	type FullInterventionDefinitionViewModel,
	type InterventionDefinitionViewModel
} from '@generated';
import { SelectFormField } from '~/shared/forms/controls/fields/SelectFormField';
import { DialogTrigger, Pressable } from 'react-aria-components';
import { Popover } from '~/shared/components/Popover';
import { FormFields } from '../../forms/FormFields';
import { DatePickerFormField } from '~/shared/forms/controls/fields/date-fields/DatePickerFormField';
import { useForm } from '~/shared/forms/FormContext';
import { DisciplineChipButton } from '~/libraries/disciplines/DisciplineChipButton';
import { isFullInterventionDefinition } from '../util';

export function InterventionDefinitionFormBody({
	interventionDefinition
}: {
	interventionDefinition: FullInterventionDefinitionViewModel | InterventionDefinitionViewModel;
}) {
	const { formHelpers } = useForm();
	const interventionDisciplines =
		interventionDefinition.restrictedToClinicalTypes && interventionDefinition.restrictedToClinicalTypes.length > 0
			? interventionDefinition.restrictedToClinicalTypes.map(x => {
					return x.disciplineId;
			  })
			: DisciplineOptions.map(x => x.id);

	const assignedDisciplineIds = formHelpers?.values?.assignedDisciplineIds || [];
	const homeHealthAideAssigned = assignedDisciplineIds.includes(DisciplineEnum.HomeHealthAide);
	const toggleDisciplineId = (disciplineId: number) => {
		if (formHelpers.readOnly) return;
		if (assignedDisciplineIds.includes(disciplineId)) {
			formHelpers?.setFieldValue('assignedDisciplineIds', [...assignedDisciplineIds.filter((x: number) => x !== disciplineId)], true);
		} else {
			formHelpers?.setFieldValue('assignedDisciplineIds', [...assignedDisciplineIds, disciplineId], true);
		}
	};

	return (
		<div className="mt-5 flex flex-col gap-4">
			{isFullInterventionDefinition(interventionDefinition) && (
				<div className="w-full max-w-3xl space-y-4">
					<FormFields
						formControls={interventionDefinition.definitionFormControls}
						formFields={interventionDefinition.definitionFormFields}
					/>
				</div>
			)}
			<div className="grid grid-cols-5 gap-4">
				<DatePickerFormField name="projectedStartDate" />
				<DatePickerFormField name="projectedEndDate" />
				<div className="col-span-2 flex flex-col justify-center">
					<span className="mb-2 block text-xs text-gray-500">{formHelpers.readOnly ? 'Selected' : 'Select'} disciplines:</span>
					<div className="flex items-center gap-1.5">
						{interventionDisciplines.map(disciplineId => {
							//does the workflows the intervention points to include this discipline.

							//whether it's part of the standard visit or not.
							const existsOnVisitWorkflow =
								interventionDefinition.routineFrequencyDisciplines.filter(x => x.id === disciplineId).length > 0;
							const isSelected = assignedDisciplineIds.includes(disciplineId);
							const disciplineOption = DisciplineOptions.find(x => x.id === disciplineId)!;
							if (existsOnVisitWorkflow) {
								return (
									<DialogTrigger key={disciplineId}>
										<Pressable>
											<DisciplineChipButton
												key={disciplineId}
												id={disciplineId}
												abbreviation={disciplineOption.abbreviation}
												selected
											/>
										</Pressable>

										<Popover showArrow className="max-w-xs p-4">
											Exists on Base Discipline Visit
										</Popover>
									</DialogTrigger>
								);
							}
							return (
								<DisciplineChipButton
									key={disciplineId}
									id={disciplineId}
									abbreviation={disciplineOption.abbreviation}
									selected={isSelected}
									isDisabled={formHelpers.readOnly}
									onClick={() => toggleDisciplineId(disciplineId)}
								/>
							);
						})}
					</div>
				</div>
				{homeHealthAideAssigned && (
					<SelectFormField options={HomeHealthAideInterventionFrequencyTypeOptions} name="homeHealthAideInterventionFrequencyTypeId" />
				)}
			</div>
		</div>
	);
}
